import { Form as FormikForm, Formik, FormikValues } from 'formik';
import React, { FC, useRef } from 'react';
import { useMutation } from 'react-query';
import * as yup from 'yup';
import arrayWrap from '../../../helpers/array-wrap';
import scrollToElement from '../../../helpers/scroll-to';
import { FormFieldType } from '../../../models/form-field-type';
import FormFieldTypeIdEnum from '../../../models/form-field-type-id-enum';
import { FormType } from '../../../models/form-type';
import submitForm from '../../../services/content-api/endpoints/form/submit';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import Field from './components/field';
import usePushFormikFormToDataLayer from '../../../hooks/use-push-formik-form-to-data-layer/use-push-formik-form-to-data-layer';

const Form: FC<FormType> = ({
  align,
  description,
  headline,
  selectedForm,
  successMessage,
}) => {
  const containerRef = useRef<HTMLDivElement>();

  let alignment = '';
  if (align === 'center') {
    alignment = 'mx-auto ';
  } else if (align === 'right') {
    alignment = 'ml-auto ';
  }

  const fields: FormFieldType[] =
    selectedForm?.pages?.reduce((accumulatedFields, page) => {
      page.fieldSets.map((fieldSet) =>
        fieldSet.containers.forEach((container) => {
          container.fields.forEach((field) => {
            accumulatedFields.push(field);
          });
        })
      );

      return accumulatedFields;
    }, []) || [];

  const fieldSets: {
    id: string;
    caption: string | null;
    fields: FormFieldType[];
  }[] =
    selectedForm?.pages?.reduce((accumulatedFieldSets, page) => {
      const newFieldSets = [];

      page.fieldSets.forEach((fieldset) => {
        const fieldSetFields = [];

        fieldset.containers.forEach((container) =>
          container.fields.forEach((field) => fieldSetFields.push(field))
        );

        newFieldSets.push({
          id: fieldset.id,
          caption: fieldset.caption,
          fields: fieldSetFields,
        });
      });

      return [...accumulatedFieldSets, ...newFieldSets];
    }, []) || [];

  const arrayFields = [
    FormFieldTypeIdEnum.CheckboxGroup,
    FormFieldTypeIdEnum.Upload,
  ];

  const initialValues = fields.reduce(
    (accumulatedInitialValues, field) => ({
      ...accumulatedInitialValues,
      [field.id]: arrayFields.includes(field.fieldTypeId)
        ? []
        : field.settings?.defaultValue || '',
    }),
    {}
  );

  const validationSchema = yup.object(
    fields.reduce((accumulatedInitialValues, field: FormFieldType) => {
      let validator = arrayFields.includes(field.fieldTypeId)
        ? yup.array()
        : yup.string();

      if (field.mandatory) {
        validator = validator.required(
          field.requiredErrorMessage || `${field.caption} is required`
        );

        if (
          validator as yup.StringSchema<string, Record<string, unknown>, string>
        ) {
          validator = (
            validator as yup.StringSchema<
              string,
              Record<string, unknown>,
              string
            >
          ).trim(field.requiredErrorMessage || `${field.caption} is required`);
        }
      }

      if (field.regex) {
        validator = (
          validator as yup.StringSchema<string, Record<string, unknown>, string>
        ).matches(new RegExp(field.regex, 'gi'), field.invalidErrorMessage);
      }

      return {
        ...accumulatedInitialValues,
        [field.id]: validator,
      };
    }, {})
  );

  const formMutation = useMutation(async (values: FormikValues) =>
    submitForm(
      selectedForm.id,
      Object.keys(values).map((fieldId) => ({
        field: fields.find((field) => field.id === fieldId),
        values: arrayWrap(values[fieldId]),
      }))
    )
  );

  const formContext = usePushFormikFormToDataLayer(
    headline,
    formMutation.isSuccess,
    (values) =>
      Object.keys(values || {}).reduce(
        (accumulated, key) => ({
          ...accumulated,
          [fields.find((field) => field.id === key).alias || 'unknown']:
            values[key],
        }),
        {}
      )
  );

  return (
    <div
      ref={containerRef}
      className={
        `${alignment} ` +
        'px-0 md:px-10 py-component-spacing-mobile lg:py-component-spacing w-full lg:w-7/12 xl:w-6/12'
      }
    >
      {headline !== '-' && (
        <h4 className="page-heading-four text-center mb-8">{headline}</h4>
      )}

      {description && (
        <p className="mb-12 text-center text-grey-600">{description}</p>
      )}

      {formMutation.isSuccess && selectedForm && (
        <div className="flex flex-col items-center">
          <div className="w-28 h-28 mb-8 flex items-center justify-center rounded-full bg-light-blue">
            <IcomoonIcon name="check" size="5xl" color="french-blue" />
          </div>

          <h6 className="page-heading-six">{successMessage}</h6>
        </div>
      )}

      {!formMutation.isSuccess && selectedForm && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => formMutation.mutateAsync(values)}
          innerRef={formContext}
        >
          <FormikForm>
            <div className="grid gap-8">
              {fieldSets.map((fieldSet) => (
                <div key={fieldSet.id} className="grid gap-8">
                  {fieldSet.caption && (
                    <h3 className="font-black font-display leading-tight text-3xl tracking-normal">
                      {fieldSet.caption}
                    </h3>
                  )}

                  <div className="grid gap-4">
                    {fieldSet.fields.map((field) => (
                      <Field key={field.id} field={field} form={selectedForm} />
                    ))}
                  </div>
                </div>
              ))}

              {formMutation.isError && (
                <p className="text-invalid text-center">
                  An unkown error occurred while submitting the form, please try
                  again.
                </p>
              )}

              <div className="mt-8 flex justify-center">
                <ButtonPrimary
                  buttonType="submit"
                  name={selectedForm.submitLabel}
                  disabled={formMutation.isLoading}
                  className={formMutation.isLoading ? 'animate-pulse' : null}
                  onClick={() => {
                    const isNearTop =
                      containerRef.current.getBoundingClientRect().top +
                        document.documentElement.scrollTop <
                      250;

                    scrollToElement(
                      isNearTop
                        ? { ref: document.body }
                        : {
                            ref: containerRef.current,
                            offsetHeight: 100,
                          }
                    );
                  }}
                />
              </div>
            </div>
          </FormikForm>
        </Formik>
      )}
    </div>
  );
};

export default Form;
